// src/components/Planner/NewTaskModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStaff } from "../../contexts/StaffContext";

const STAGES = [
  { key: "forming", label: "Forming" },
  { key: "drying", label: "Drying" },
  { key: "bisque", label: "Bisque" },
  { key: "glazing", label: "Glazing" },
  { key: "glazeFiring", label: "Glaze Firing" },
  { key: "qcPack", label: "QC & Pack" },
  { key: "dispatch", label: "Dispatch" },
];

function toISO(d) {
  if (!d) return undefined;
  const dt = new Date(d);
  return new Date(
    dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours(), dt.getMinutes(), 0
  ).toISOString();
}

function stageToResource(k) {
  if (k === "glazeFiring" || k === "bisque") return "kiln";
  if (k === "forming" || k === "drying" || k === "glazing") return "bench";
  return "general";
}

// default durations/buffers (hours)
const DEFAULT_DURATIONS_H = { forming: 2, drying: 48, bisque: 24, glazing: 3, glazeFiring: 24, qcPack: 2, dispatch: 0.5 };
const DEFAULT_BUFFERS_H   = { forming: 0, drying: 0,  bisque: 4,  glazing: 12, glazeFiring: 4,  qcPack: 2, dispatch: 0 };

// Flexible deleter: supports fn(id) | fn([id]) | fn({id})
function callDeleteAny(ctxFns, id) {
  if (!id) return;
  const { deleteEvents, deleteEvent, removeEvents, removeEvent } = ctxFns || {};
  const tryCall = (fn) => {
    if (typeof fn !== "function") return false;
    try { fn(id); return true; } catch {}
    try { fn([id]); return true; } catch {}
    try { fn({ id }); return true; } catch {}
    return false;
  };
  return tryCall(deleteEvents) || tryCall(deleteEvent) || tryCall(removeEvents) || tryCall(removeEvent);
}

export default function NewTaskModal({ isOpen, onClose, seed = {} }) {
  const planner = usePlanner();
  const { upsertEvents } = planner;
  const { staffList = [] } = useStaff();

  const editId = seed?.id || seed?._id || seed?.uuid || null;
  const isEditing = Boolean(editId);

  const [title, setTitle] = useState(seed.title || "");
  const [start, setStart] = useState(seed.start ? seed.start.slice(0, 16) : new Date().toISOString().slice(0, 16));
  const [end, setEnd] = useState(
    seed.end ? seed.end.slice(0, 16) : new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 16)
  );
  const [stage, setStage] = useState(seed.stage || "forming");
  const [qty, setQty] = useState(seed.qty || 1);
  const [batch, setBatch] = useState(seed.batch || "");
  const [category, setCategory] = useState(seed.category || "production");
  const [taskType, setTaskType] = useState(seed.taskType || stage);
  const [productId, setProductId] = useState(seed.productId || "");
  const [assigneeId, setAssigneeId] = useState(seed.assigneeId || "");

  // chain creation
  const [makeChain, setMakeChain] = useState(false);
  const [durH, setDurH] = useState({ ...DEFAULT_DURATIONS_H });
  const [bufH, setBufH] = useState({ ...DEFAULT_BUFFERS_H });

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "enter") save();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, title, start, end, stage, qty, batch, category, taskType, productId, assigneeId, makeChain, durH, bufH]);

  if (!isOpen) return null;

  function clampEndAfterStart(startISO, endISO) {
    const s = new Date(startISO).getTime();
    const e = new Date(endISO).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e)) return endISO;
    if (e <= s) return new Date(s + 60 * 60 * 1000).toISOString().slice(0, 16);
    return endISO;
  }

  function mkEvent({ _id, _title, _stage, _startISO, _durHours }) {
    const startMs = new Date(_startISO).getTime();
    const endMs = startMs + _durHours * 3600 * 1000;
    return {
      id: _id || `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title: _title,
      start: new Date(startMs).toISOString(),
      end: new Date(endMs).toISOString(),
      status: "planned",
      tags: [_stage],
      resource: stageToResource(_stage),
      qty: Number(qty || 1),
      category,
      taskType: _stage,
      productId: productId || undefined,
      assigneeId: assigneeId || undefined,
      color: undefined,
      dependencies: [],
      batch: batch?.trim() || undefined,
      source: "manual",
    };
  }

  function saveSingle() {
    const safeEnd = clampEndAfterStart(start, end);
    const ev = mkEvent({
      _id: editId || undefined,
      _title: title?.trim() || "Untitled task",
      _stage: stage,
      _startISO: toISO(start),
      _durHours: Math.max(0.25, Number(((new Date(safeEnd) - new Date(start)) / 3600000).toFixed(4))),
    });
    upsertEvents([ev]);
    onClose?.();
  }

  function saveChain() {
    const stageIndex = Math.max(0, STAGES.findIndex((s) => s.key === stage));
    const chain = STAGES.slice(stageIndex);

    let cursor = new Date(toISO(start)).getTime();
    const out = [];
    let prevId = null;

    for (const s of chain) {
      const dH = Math.max(0.1, Number(durH[s.key] ?? DEFAULT_DURATIONS_H[s.key] ?? 1));
      const ev = mkEvent({
        _title: `${title?.trim() || "Untitled"} — ${s.label}`,
        _stage: s.key,
        _startISO: new Date(cursor).toISOString(),
        _durHours: dH,
      });
      if (prevId) ev.dependencies = [prevId];
      out.push(ev);

      const bufferH = Math.max(0, Number(bufH[s.key] ?? 0));
      cursor += (dH + bufferH) * 3600 * 1000;
      prevId = ev.id;
    }

    upsertEvents(out);
    onClose?.();
  }

  function save() {
    if (!title.trim()) return alert("Please provide a title.");
    if (!start) return alert("Please choose a start date/time.");
    if (!makeChain && !end) return alert("Please choose an end date/time.");
    makeChain ? saveChain() : saveSingle();
  }

  async function handleDelete() {
    if (!isEditing) return;
    const ok = window.confirm("Delete this task?");
    if (!ok) return;
    callDeleteAny(planner, editId);
    onClose?.();
  }

  const staffOptions = useMemo(
    () => [
      { id: "", label: "— Unassigned —" },
      ...staffList.map((s) => ({ id: String(s.id), label: s.name || String(s.id) })),
    ],
    [staffList]
  );

  // Theme-aware input class (light by default, dark overrides)
  const inputCls =
    "w-full rounded-lg px-3 py-2 border bg-black/5 border-black/10 text-black placeholder-black/50 " +
    "dark:bg-black/30 dark:border-white/10 dark:text-white dark:placeholder-white/60";

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl border shadow-xl p-5 bg-white text-black border-black/10
                      dark:bg-[#111318] dark:text-white dark:border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold">{isEditing ? "Edit Task" : "New Task"}</h3>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded border border-black/10 hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10"
            title="Close (Esc)"
          >
            Close
          </button>
        </div>

        {/* Title + Batch */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs opacity-70 mb-1">Title</label>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task name" />
          </div>
          <div>
            <label className="block text-xs opacity-70 mb-1">Batch (optional)</label>
            <input className={inputCls} value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="e.g. B24-09" />
          </div>
        </div>

        {/* Start/End */}
        {!makeChain && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs opacity-70 mb-1">Start</label>
              <input
                type="datetime-local"
                className={inputCls}
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                  setEnd((prev) => clampEndAfterStart(e.target.value, prev));
                }}
              />
            </div>
            <div>
              <label className="block text-xs opacity-70 mb-1">End</label>
              <input
                type="datetime-local"
                className={inputCls}
                value={end}
                onChange={(e) => setEnd(clampEndAfterStart(start, e.target.value))}
              />
            </div>
          </div>
        )}

        {/* If chain: only Start; end is derived */}
        {makeChain && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs opacity-70 mb-1">Start</label>
              <input type="datetime-local" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="flex items-end">
              <div className="text-xs opacity-70">End is calculated from durations & buffers</div>
            </div>
          </div>
        )}

        {/* Stage + Qty */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs opacity-70 mb-1">Stage</label>
            <select
              className={inputCls}
              value={stage}
              onChange={(e) => { setStage(e.target.value); setTaskType(e.target.value); }}
              disabled={makeChain}
              title={makeChain ? "When creating a chain, it starts at this stage and includes the ones after it." : ""}
            >
              {STAGES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs opacity-70 mb-1">Qty</label>
            <input type="number" min={1} className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
        </div>

        {/* Category + Task Type */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs opacity-70 mb-1">Category</label>
            <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="production">Production</option>
              <option value="orders">Orders</option>
              <option value="admin">Admin</option>
              <option value="workshop">Workshop</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>
          <div>
            <label className="block text-xs opacity-70 mb-1">Task Type</label>
            <input
              className={inputCls}
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              placeholder="e.g. glazing, invoicing"
              disabled={makeChain}
            />
          </div>
        </div>

        {/* Product + Assignee */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs opacity-70 mb-1">Product (optional)</label>
            <input className={inputCls} value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="product id or name" />
          </div>
          <div>
            <label className="block text-xs opacity-70 mb-1">Assignee</label>
            <select className={inputCls} value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
              {staffOptions.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </div>
        </div>

        {/* Chain builder */}
        <div className="mt-4 rounded-lg border border-black/10 dark:border-white/10">
          <div className="flex items-center justify-between px-3 py-2 rounded-t-lg bg-black/5 dark:bg-white/5">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={makeChain} onChange={(e) => setMakeChain(e.target.checked)} />
              Generate dependent stages after the selected start stage
            </label>
            <span className="text-[11px] opacity-70">Each stage becomes a task with a dependency on the previous.</span>
          </div>

          {makeChain && (
            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {STAGES.map((s) => (
                <div key={s.key} className="rounded-lg p-3 border bg-black/5 border-black/10 dark:bg-black/20 dark:border-white/10">
                  <div className="text-sm font-medium mb-2">{s.label}</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <label className="block">
                      <div className="text-xs opacity-70 mb-1">Duration (h)</div>
                      <input
                        type="number" min={0.1} step={0.1}
                        value={durH[s.key]}
                        onChange={(e) => setDurH((prev) => ({ ...prev, [s.key]: Number(e.target.value) }))}
                        className={"w-full rounded px-2 py-1.5 border bg-black/5 border-black/10 dark:bg-black/30 dark:border-white/10"}
                      />
                    </label>
                    <label className="block">
                      <div className="text-xs opacity-70 mb-1">Buffer after (h)</div>
                      <input
                        type="number" min={0} step={0.1}
                        value={bufH[s.key]}
                        onChange={(e) => setBufH((prev) => ({ ...prev, [s.key]: Number(e.target.value) }))}
                        className={"w-full rounded px-2 py-1.5 border bg-black/5 border-black/10 dark:bg-black/30 dark:border-white/10"}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-5">
          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              title="Delete this task"
            >
              Delete
            </button>
          )}
          <button
            className="px-3 py-2 rounded border border-black/10 hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-3 py-2 rounded bg-orange-500 hover:bg-orange-600 text-white"
            onClick={save}
            title="Save (⌘/Ctrl+Enter)"
          >
            {makeChain ? "Save Chain" : "Save Task"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
